import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { KeyRound, ExternalLink, Info } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { dec } from "@/lib/formatters";

const SIZES = ["30", "40", "50", "60", "70"];

const EstimateBadge = () => (
  <Badge variant="outline" className="border-[#E8A34A] text-[#B8741F] bg-[#FDF6EA] text-[10px] uppercase tracking-wide" data-testid="leie-estimate-badge">
    Estimat
  </Badge>
);

const SourceLink = ({ table }) => (
  <a href={`https://www.ssb.no/statbank/table/${table}`} target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
    SSB {table} <ExternalLink className="w-3 h-3" />
  </a>
);

// data: { average: {zone, zone_label, estimate, note, series[]}, national[], predicted[], population_used }
const LeieSection = ({ data }) => {
  const avg = data?.average;
  const series = avg?.series || [];
  const latest = series[series.length - 1];
  const prev = series[series.length - 2];
  const change = latest && prev?.monthly ? ((latest.monthly - prev.monthly) / prev.monthly) * 100 : null;
  const nationalByYear = Object.fromEntries((data?.national || []).map((n) => [n.year, n.monthly]));
  const chartData = series.map((s) => ({ year: s.year, Prissone: s.monthly, "Heile landet": nationalByYear[s.year] ?? null }));
  const anyEstimate = avg?.estimate || (data?.predicted || []).some((p) => p.estimate);
  const notes = [...new Set([avg?.note, ...(data?.predicted || []).map((p) => p.note)].filter(Boolean))];

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="leie-section" id="leie">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-[#103766]" />
          Leigekostnader for toromsbustad
          {anyEstimate && <EstimateBadge />}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          SSBs leigemarknadsundersøking blir publisert per prissone, ikkje per kommune. Tala under gjeld prissonen kommunen er plassert i.
        </p>
      </CardHeader>
      <CardContent className="pt-6">
        {!latest ? (
          <div className="h-[140px] flex items-center justify-center text-[#8AA4BF]" data-testid="leie-empty">
            Leigedata blir henta frå SSB – last sidan på nytt om litt
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            <div className="xl:col-span-2 space-y-4">
              <div className="p-4 bg-[#F6F8FB] rounded-lg">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">Gjennomsnittleg månadsleige {latest.year}, 2 rom</p>
                <p className="text-3xl font-light text-[#1F2A36]" data-testid="leie-monthly">{fmt(latest.monthly)} kr</p>
                <p className="text-sm text-[#4B647F] mt-1">{fmt(latest.per_m2_year)} kr per m² per år</p>
                {change != null && (
                  <p className={`text-sm mt-1 ${change > 0 ? "text-[#D35E4E]" : "text-[#3F8A86]"}`}>
                    {change > 0 ? "+" : ""}{dec(change, 1)} % frå {prev.year}
                  </p>
                )}
                <div className="text-xs text-[#4B647F] mt-3 flex items-center gap-2 flex-wrap" data-testid="leie-zone">
                  Prissone: <span className="font-medium text-[#1F2A36]">{avg.zone_label}</span>
                  {avg.estimate && <EstimateBadge />}
                </div>
              </div>
              <div className="h-[240px]" data-testid="leie-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    {endringsLinjer(chartData)}
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                    <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                    <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} domain={["auto", "auto"]} />
                    <Tooltip formatter={(v) => `${fmt(v)} kr/mnd`} contentStyle={tooltipStyle} />
                    <Legend />
                    <Line type="monotone" dataKey="Prissone" name={avg.zone_label} stroke="#103766" strokeWidth={2} dot={{ fill: "#103766", r: 3 }} />
                    <Line type="monotone" dataKey="Heile landet" stroke="#E8A34A" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: "white", stroke: "#E8A34A", r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="xl:col-span-3">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">
                Predikert månadsleige {data.predicted[0]?.latest_year} etter storleik (kr)
              </p>
              <div className="overflow-x-auto">
                <Table data-testid="leie-predicted-table">
                  <TableHeader>
                    <TableRow className="bg-[#F6F8FB]">
                      <TableHead className="text-[#4B647F]">Prissone</TableHead>
                      {SIZES.map((s) => <TableHead key={s} className="text-[#4B647F] text-right">2 rom, {s} m²</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.predicted.map((p) => (
                      <TableRow key={p.zone} className="hover:bg-[#F6F8FB]/50" data-testid={`leige-predicted-${p.zone}`}>
                        <TableCell className="text-sm">
                          <span className="flex items-center gap-2 flex-wrap">{p.zone_label}{p.estimate && <EstimateBadge />}</span>
                        </TableCell>
                        {SIZES.map((s) => <TableCell key={s} className="text-right text-sm tabular-nums">{fmt(p.sizes?.[s])}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {notes.length > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-[#FDF6EA] border border-[#F3DDB3] text-xs text-[#7A5A1E] space-y-1" data-testid="leie-estimate-note">
                  {notes.map((n) => (
                    <p key={n} className="flex gap-2"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{n}</p>
                  ))}
                  {data.population_used && avg.estimate && (
                    <p className="pl-5">Folketal brukt i anslaget: {fmt(data.population_used)}.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <p className="text-xs text-[#8AA4BF] mt-4 text-center">
          Kjelde: <SourceLink table="09895" /> (gjennomsnittleg leige) og <SourceLink table="09897" /> (predikert leige). Leigemarknadsundersøkinga til SSB.
        </p>
      </CardContent>
    </Card>
  );
};

export default LeieSection;
